import { useState, useEffect } from 'react';
import type { Section, Option, DefaultOption } from '../types';
import { useAuth } from './useAuth';
import { fetchProjectData } from '../services/projectDataApi';

/**
 * Loads the logged-in client's project data from the API.
 * Does nothing while unauthenticated — AppContent shows the login page then,
 * but calls this hook unconditionally, so the gate lives here.
 */
export const useData = () => {
  const { token, isAuthenticated, isAdmin, signOut } = useAuth();
  const [companyName, setCompanyName] = useState<string>('');
  const [sections, setSections] = useState<Section[]>([]);
  const [options, setOptions] = useState<Option[]>([]);
  const [defaultOptions, setDefaultOptions] = useState<DefaultOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Admin accounts have no client project of their own — nothing to fetch
    if (!isAuthenticated || !token || isAdmin) {
      setCompanyName('');
      setSections([]);
      setOptions([]);
      setDefaultOptions([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    let is_cancelled = false;

    const loadData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await fetchProjectData(token);
        if (is_cancelled) {
          return;
        }

        // Map from snake_case (API) to camelCase (application)
        setCompanyName(data.company_name || '');
        setSections(data.sections);
        setOptions(data.options);
        setDefaultOptions(data.default_options || []);
        setIsLoading(false);
      } catch (err) {
        if (is_cancelled) {
          return;
        }

        const status = (err as Error & { status?: number }).status;
        if (status === 401) {
          // Stale or revoked token — drop back to the login page
          signOut();
        } else {
          setError((err as Error).message || 'Failed to load project data');
        }
        setIsLoading(false);
        console.error('Error loading data:', err);
      }
    };

    loadData();

    return () => {
      is_cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, token, isAdmin]);

  return {
    companyName,
    sections,
    options,
    defaultOptions,
    isLoading,
    error
  };
};
